/**
 * MapControls — Shared map UI components for participant interfaces.
 * Layout: narrow legend (left) | map (center) | narrow hex inspector (right)
 */

/* ── Unit type icons (inline SVG paths matching map-core.js symbols) ── */
const UNIT_ICONS: Record<string,string> = {
  ground: 'M10 8L18 8L22 10V11H10ZM2 13L5 11H20L21 13V15H3ZM2 15H22C22 18 20 19 18 19H6C4 19 2 18 2 15Z',
  naval: 'M2 16L4 12H8V10H10V8H12V6H13V8H14V10H16V12H22V14L21 16Z',
  tactical_air: 'M12 6L13 9L22 12V14L13 12V16L15 18V19L12 18L9 19V18L11 16V12L2 14V12L11 9Z',
  air_defense: 'M4 10C4 5 8 2 12 2C16 2 20 5 20 10H18C18 6 15.5 3.5 12 3.5C8.5 3.5 6 6 6 10ZM11 9H13V20H11ZM7 20H17V22H7Z',
  strategic_missile: 'M12 1L14.5 6H14V18H16V22H8V18H10V6H9.5Z',
}
const UNIT_SHORT: Record<string,string> = {
  ground:'Ground', naval:'Naval', tactical_air:'Tac. Air',
  air_defense:'Air Def.', strategic_missile:'Missile',
}

/* ── Hex info type from map postMessage ──────────────────────────────── */
export interface HexInfo {
  row: number; col: number; view: string
  owner: string; ownerName: string; ownerColor: string
  chokepoint: string | null; dieHard: boolean; nuclearSite: string | null
  occupiedBy: string | null; theaterLink?: string | null
  units: {
    unit_id: string; unit_type: string; country_id: string
    countryName: string; countryColor: string; count: number; status: string
  }[]
}

/* ── Narrow legend (left sidebar) ────────────────────────────────────── */
export function MapLegend() {
  return (
    <div className="bg-card border border-border rounded-lg p-2 w-[120px] flex-shrink-0 overflow-hidden">
      <h4 className="font-body text-[9px] font-semibold text-text-muted uppercase tracking-wider mb-1.5">Legend</h4>
      <div className="flex flex-col gap-1">
        {Object.entries(UNIT_SHORT).map(([k, label]) => (
          <div key={k} className="flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-text-secondary flex-shrink-0"><path d={UNIT_ICONS[k]} fill="currentColor"/></svg>
            <span className="font-body text-[10px] text-text-primary leading-tight">{label}</span>
          </div>
        ))}
        <div className="border-t border-border my-0.5"/>
        <div className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 flex-shrink-0 border border-dashed border-warning rounded-sm"/>
          <span className="font-body text-[10px] text-text-primary leading-tight">Choke</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 flex-shrink-0 border border-dashed border-danger rounded-sm"/>
          <span className="font-body text-[10px] text-text-primary leading-tight">Die-Hard</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center text-warning text-[10px]">☢</span>
          <span className="font-body text-[10px] text-text-primary leading-tight">Nuclear</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 flex-shrink-0 bg-card-2 border border-warning rounded-sm"/>
          <span className="font-body text-[10px] text-text-primary leading-tight">Theater</span>
        </div>
      </div>
    </div>
  )
}

/* ── Narrow hex inspector (right sidebar) ────────────────────────────── */
export function HexInspector({ info }: { info: HexInfo | null }) {
  if (!info) return (
    <div className="bg-card border border-border rounded-lg p-2 w-[140px] flex-shrink-0 overflow-hidden">
      <h4 className="font-body text-[9px] font-semibold text-text-muted uppercase tracking-wider mb-1">Inspector</h4>
      <p className="font-body text-[10px] text-text-muted italic leading-tight">Click a hex to inspect</p>
    </div>
  )
  const isSea = info.owner === 'sea'
  return (
    <div className="bg-card border border-border rounded-lg p-2 w-[140px] flex-shrink-0 overflow-hidden">
      <h4 className="font-body text-[9px] font-semibold text-text-muted uppercase tracking-wider mb-1">Inspector</h4>
      <div className="flex items-center gap-1.5 mb-1">
        {!isSea && <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: info.ownerColor }}/>}
        <span className="font-heading text-[11px] font-semibold text-text-primary leading-tight truncate">
          {info.chokepoint || (isSea ? 'Sea' : info.ownerName)}
        </span>
      </div>
      <div className="font-mono text-[9px] text-text-muted mb-1">{info.row},{info.col}</div>
      {info.occupiedBy && <div className="font-body text-[10px] text-danger leading-tight mb-0.5">⚔ Occupied: {info.occupiedBy}</div>}
      {info.chokepoint && <div className="font-body text-[10px] text-warning leading-tight mb-0.5">⬡ Chokepoint</div>}
      {info.dieHard && <div className="font-body text-[10px] text-danger leading-tight mb-0.5">⬡ Die-Hard</div>}
      {info.nuclearSite && <div className="font-body text-[10px] text-warning leading-tight mb-0.5">☢ Nuclear</div>}
      {info.units.length > 0 ? (
        <div className="flex flex-col gap-0.5 mt-1 border-t border-border pt-1">
          {info.units.map((u, i) => (
            <div key={i} className="flex items-center gap-1">
              <svg viewBox="0 0 24 24" className="w-3 h-3 flex-shrink-0" style={{ color: u.countryColor }}>
                <path d={UNIT_ICONS[u.unit_type] || ''} fill="currentColor"/>
              </svg>
              <span className="font-body text-[10px] text-text-primary truncate">{u.countryName}</span>
              {u.count > 1 && <span className="font-mono text-[9px] text-warning ml-auto flex-shrink-0">×{u.count}</span>}
            </div>
          ))}
        </div>
      ) : (
        <p className="font-body text-[10px] text-text-muted italic mt-1">No units</p>
      )}
    </div>
  )
}
